/**
 * SDK 硬件缓存层 - HardwareCache (Phase 2)
 *
 * 在一次连接生命周期内,以 `(address, length)` 为 key,缓存"`sendAndWait` 返回的
 * 字节快照"。命中时 `createSendRead` 会基于 `entry.data` 重建 `DataView` 喂给 parser,
 * 业务层无法察觉缓存命中(详见 design `Components §2`)。
 *
 * 本文件只实现 Phase 2 的 KV 语义与 disable/enable 状态机,**不涉及**:
 * - tag 索引 / 地址区间索引 (Phase 3 `TaggedCache` 的职责)
 * - 上报回填 (Phase 4 `ReportBackfill` 的职责)
 * - `__enableShadowRead` 双读对比 (Task 7 横切补齐)
 * - `BaseDevice` / `createSendRead` / `createSendWrite` 接入 (Task 3.2 / 3.3 / 3.4)
 *
 * --- 关键约束 (与 design / requirements 一一对齐) ---
 *
 * 1. **写入即拷贝独立内存** (Requirement 2.9, design Property 1):
 *    `set` 内部对入参 `data` 使用 `new Uint8Array(data)` 拷贝出独立 ArrayBuffer,
 *    避免与底层 `DataView.buffer` 共享内存,防止后续硬件读原地覆盖污染缓存。
 *
 * 2. **disable 状态机** (Requirement 2.7, design `Error Handling §4`):
 *    - 禁用时:`get` 返回 `undefined`(等同未命中);`set` 静默丢弃;`delete` 静默丢弃
 *    - 禁用时:`clearAll()` **仍正常执行**(用户调 `clearAll` 是显式意图,
 *      即使禁用也要清空旧数据)
 *    - 禁用不清空已有缓存,`enable()` 调用后立即恢复读写
 *
 * 3. **防御性 try/catch** (Requirement 5.8, 5.9, design `Error Handling §6`):
 *    所有公开方法在最外层包 `try/catch`,捕获后 `console.error` 输出诊断信息,
 *    退化为"等同未命中"或"等同未失效"行为,**不向调用栈传播异常**,确保缓存层
 *    内部 bug 不会让设备整体不可用。
 *
 * 4. **只缓字节,不缓对象** (design `Components §2`):
 *    `CacheEntry.data` 是 `Uint8Array`,不是 parser 解析后的业务对象。Parser 在
 *    每次 `sendRead` 命中时跑一次,业务层每次拿到的都是新对象。
 *
 * 5. **粗失效是 Phase 2 的明确选择** (Requirement 2.4):
 *    `clearAll()` 在 `sendWrite` 成功后被调用(由 `createSendWrite` 在 Task 3.4
 *    接入)。Phase 3 升级后由 `TaggedCache.invalidateByAddressRange` 取代,但
 *    `clearAll()` 仍保留作为 `close()` 兜底与逃生口。
 *
 * 6. **生命周期** (Requirement 2.6):
 *    `BaseDevice.close()` 内会调用 `cache.clearAll()`(由 Task 3.2 接入),
 *    本类自身不感知 `BaseDevice`。
 *
 * @packageDocumentation
 */

import type { CacheEntry, CacheKey } from './types'

/**
 * 会话级 KV 硬件缓存。
 *
 * 持有 `Map<CacheKey, CacheEntry>` 作为内部存储,以及一个布尔开关 `enabled`
 * 控制 disable/enable 状态。每个 `BaseDevice` 实例持有一个 `HardwareCache` 实例,
 * 跨设备互不影响。
 *
 * 详见:
 * - design.md `Components §2` (接口契约)
 * - design.md `Data Models §1` (CacheEntry 结构与字节快照规则)
 * - design.md `Error Handling §4 / §6` (disable 与防御性 try/catch)
 * - requirements.md 2.1 / 2.2 / 2.3 / 2.4 / 2.7 / 2.9 / 5.4 / 5.5 / 5.7 / 5.8 / 5.9 / 5.11
 */
export class HardwareCache {
  /**
   * 内部 KV 存储。`Map<CacheKey, CacheEntry>`,保持插入顺序便于调试。
   *
   * 直接使用 `Map` 而非普通对象,避免 prototype 污染,且在频繁 `delete`
   * 场景下性能与内存表现更稳定。
   */
  private readonly entries = new Map<CacheKey, CacheEntry>()

  /**
   * 缓存启用状态。`true` 表示正常 KV 读写;`false` 表示禁用(get 返回 undefined,
   * set/delete 静默丢弃)。`clearAll()` 不受此开关影响。
   *
   * 默认启用(Requirement 2.7 隐含语义:无显式 disable 调用时 `sendRead` 走缓存)。
   */
  private enabled = true

  /**
   * 命中返回 `CacheEntry`,未命中返回 `undefined`。
   *
   * - 缓存被禁用时返回 `undefined`(等同未命中, Requirement 2.7)
   * - 内部异常时退化为"等同未命中",不向上抛(Requirement 5.8)
   *
   * **不返回 `data` 副本** —— `createSendRead` 命中后会基于 `entry.data` 重建
   * `DataView` 喂给 parser,parser 输出是全新对象,业务层永远拿不到 `entry` 本身。
   *
   * @param key 由 `makeKey(address, length)` 生成的缓存键
   * @returns 命中的 `CacheEntry`,或 `undefined`
   */
  get(key: CacheKey): CacheEntry | undefined {
    try {
      if (!this.enabled) return undefined
      return this.entries.get(key)
    } catch (err) {
      // 防御性退化为"等同未命中" (Requirement 5.8 / 5.9)
      console.error('[HardwareCache] get() internal error, degraded to miss:', err)
      return undefined
    }
  }

  /**
   * 写入缓存条目。
   *
   * **必做的独立内存拷贝** (Requirement 2.9, design Property 1):
   * 入参 `data` 通常来自 `sendAndWait` 返回的 `DataView` 字节切片,与底层
   * `ArrayBuffer` 共享内存。本方法内部用 `new Uint8Array(data)` 拷贝出全新
   * `ArrayBuffer` 后再存入 `entries`,避免后续硬件读原地覆盖污染缓存。
   *
   * 行为:
   * - 缓存被禁用时 **静默丢弃**,不抛错(Requirement 2.7)
   * - 内部异常时 **静默丢弃**,console.error 后返回(Requirement 5.8)
   * - `ts` 由本方法内部填写,不接受外部覆盖(简化契约)
   *
   * Phase 2 写入不携带 `tags` / `addressStart` / `length`(对应 `CacheEntry`
   * 中三个可选字段保持 `undefined`);Phase 3 `TaggedCache` 会扩展此能力。
   *
   * @param key 由 `makeKey(address, length)` 生成的缓存键
   * @param data 字节快照源数据。本方法会拷贝独立内存副本,不持有 `data` 本身的引用
   */
  set(key: CacheKey, data: Uint8Array): void {
    try {
      if (!this.enabled) return
      // 独立内存拷贝: new Uint8Array(typedArray) 会创建新的 ArrayBuffer
      // 并复制源 typed array 的内容,与底层 DataView.buffer 完全隔离。
      const snapshot = new Uint8Array(data)
      const entry: CacheEntry = {
        data: snapshot,
        ts: Date.now(),
      }
      this.entries.set(key, entry)
    } catch (err) {
      // 防御性静默丢弃 (Requirement 5.8 / 5.9)
      console.error('[HardwareCache] set() internal error, dropped silently:', err)
    }
  }

  /**
   * 删除单条缓存。
   *
   * - 缓存被禁用时 **静默丢弃**,不抛错(Requirement 2.7,与 set 对称)
   * - key 不存在时 no-op,不抛错
   * - 内部异常时 console.error 后返回,不向上抛(Requirement 5.8)
   *
   * @param key 要删除的缓存键
   */
  delete(key: CacheKey): void {
    try {
      if (!this.enabled) return
      this.entries.delete(key)
    } catch (err) {
      console.error('[HardwareCache] delete() internal error, ignored:', err)
    }
  }

  /**
   * 清空当前设备的全部缓存条目。
   *
   * **重要**:与 `set` / `delete` 不同,本方法在缓存被禁用时 **仍正常执行**。
   * 用户/调用方调用 `clearAll` 是显式清空意图,即使缓存当前禁用也要尊重该意图,
   * 避免遗留旧数据在 `enable()` 后被误命中(详见 design `Error Handling §4`)。
   *
   * 典型调用位置(Task 3.2 / 3.4 接入):
   * - `BaseDevice.close()` 在 `transport.close()` 之前(Requirement 2.6)
   * - `createSendWrite` 在 `sendAndWait` 成功后(Phase 2 粗失效, Requirement 2.4)
   *   注:Phase 3 上线后此处会改为 `invalidateByAddressRange`
   *
   * 内部异常退化为 no-op,console.error 后返回(Requirement 5.8)。
   */
  clearAll(): void {
    try {
      // 注意: 不检查 this.enabled —— 见 design Error Handling §4
      this.entries.clear()
    } catch (err) {
      console.error('[HardwareCache] clearAll() internal error, ignored:', err)
    }
  }

  /**
   * 当前缓存条目数量。
   *
   * 用途:
   * - 可观测性 / 业务侧调试(Requirement 5.11)
   * - 测试断言(例如 `close()` 后断言 `cache.size() === 0`)
   *
   * 本方法不受 `disable()` 影响,反映的是 `Map` 的真实大小;禁用期间已有条目
   * 仍计入此数。
   *
   * 内部异常时返回 `0`,console.error 后返回(防御性退化, Requirement 5.8)。
   */
  size(): number {
    try {
      return this.entries.size
    } catch (err) {
      console.error('[HardwareCache] size() internal error, returned 0:', err)
      return 0
    }
  }

  /**
   * 禁用缓存。
   *
   * 禁用后:
   * - `get` 返回 `undefined`(等同未命中)
   * - `set` 静默丢弃
   * - `delete` 静默丢弃
   * - `clearAll()` **仍正常执行**(显式清空意图必须被尊重)
   *
   * **不清空已有缓存** —— 这是为了支持 OTA、宏录制等场景临时关闭缓存而不丢数据。
   * `enable()` 调用后,旧缓存条目仍可被命中。
   *
   * 用作业务侧逃生口(Requirement 5.10):若发现某段时间缓存表现异常,
   * 可临时禁用,继续走纯硬件读取。
   *
   * 内部异常时 console.error 但**仍切换状态**(因为状态切换是简单赋值,
   * 几乎不可能抛错;此处的 catch 仅作防御兜底)。
   */
  disable(): void {
    try {
      this.enabled = false
    } catch (err) {
      console.error('[HardwareCache] disable() internal error:', err)
    }
  }

  /**
   * 启用缓存。
   *
   * 启用后:
   * - 之前 `disable` 期间的写入已丢失(因为禁用时 `set` 静默丢弃),
   *   下次读会真实拉硬件并重新填充
   * - 之前禁用前已存在的条目仍然有效,可被命中
   *
   * 内部异常时 console.error 但**仍切换状态**(同 `disable`)。
   */
  enable(): void {
    try {
      this.enabled = true
    } catch (err) {
      console.error('[HardwareCache] enable() internal error:', err)
    }
  }

  // ---------- Shadow Read (Requirement 5.13, Task 7.3) ----------

  /**
   * Shadow Read 开关。仅用于测试/验证 —— 启用后,缓存命中时仍发一次真实硬件读,
   * 将缓存字节与硬件返回字节做字节级对比。如果不一致则 console.error + 抛错。
   *
   * 默认关闭。关闭时为单纯布尔读,无性能开销。
   * 生产构建可 tree-shake 或运行时无副作用。
   */
  _shadowReadEnabled = false

  /**
   * 开关 Shadow Read 模式。
   *
   * @param on - `true` 启用双读对比,`false` 关闭
   */
  __enableShadowRead(on: boolean): void {
    this._shadowReadEnabled = on
  }
}
